import type {
  PersonEfteruddannelse,
  PersonEfteruddannelseIndberetning,
} from "@/interfaces/schema";
import { EfteruddannelseIndberetningstypeId } from "@/interfaces/schema";
import { roundLessons } from "@/util/number";

import { getTransferredIndberetninger } from "./getTransferredIndberetninger";

// Lektioner, der overføres UD af en periode, ligger som en Særlig-indberetning
// på den EFTERFØLGENDE periode (AIKA-domænekonvention). Overført IND i en
// periode aflæses derfor på perioden selv, overført UD på den næste.
// GraphQL-svar indeholder kun de valgte felter, så tilDato kan mangle på
// trods af skematypen — fald tilbage på tilDatoInklusiv (dagen før).
const getPeriodEndTime = (period: PersonEfteruddannelse): number => {
  const { tilDato, tilDatoInklusiv } = period.periode;

  return new Date(tilDato ?? tilDatoInklusiv).getTime();
};

export const findFollowingPeriod = (
  periods: PersonEfteruddannelse[],
  period: PersonEfteruddannelse,
): PersonEfteruddannelse | undefined => {
  const periodEnd = getPeriodEndTime(period);

  return periods
    .filter(
      (candidate) =>
        candidate !== period &&
        new Date(candidate.periode.fraDato).getTime() >= periodEnd,
    )
    .sort(
      (a, b) =>
        new Date(a.periode.fraDato).getTime() -
        new Date(b.periode.fraDato).getTime(),
    )[0];
};

export const sumIndberetningLinjer = (
  indberetninger: Pick<
    PersonEfteruddannelseIndberetning,
    "personEfteruddannelseIndberetningLinje"
  >[],
): number =>
  indberetninger.reduce(
    (total, indberetning) =>
      total +
      indberetning.personEfteruddannelseIndberetningLinje.reduce(
        (linjeTotal, linje) => linjeTotal + linje.indberettedeLektioner,
        0,
      ),
    0,
  );

export const getCarriedInLessons = (period: PersonEfteruddannelse): number =>
  roundLessons(sumIndberetningLinjer(getTransferredIndberetninger(period)));

export const getSaerligIndberetninger = (period: PersonEfteruddannelse) =>
  period.personEfteruddannelseIndberetning.filter(
    (indberetning) =>
      indberetning.efteruddannelseIndberetningstype ===
      EfteruddannelseIndberetningstypeId.Saerlig,
  );

export const getTransferredOutLessons = (
  period: PersonEfteruddannelse,
  allPeriods: PersonEfteruddannelse[],
): number => {
  const followingPeriod = findFollowingPeriod(allPeriods, period);

  if (!followingPeriod) return 0;

  return roundLessons(
    sumIndberetningLinjer(getSaerligIndberetninger(followingPeriod)),
  );
};

export const getTransferredOutLessonsByType = (
  period: PersonEfteruddannelse,
  allPeriods: PersonEfteruddannelse[],
): Map<number, number> => {
  const byType = new Map<number, number>();
  const followingPeriod = findFollowingPeriod(allPeriods, period);

  if (!followingPeriod) return byType;

  getSaerligIndberetninger(followingPeriod).forEach((indberetning) => {
    indberetning.personEfteruddannelseIndberetningLinje.forEach((linje) => {
      byType.set(
        linje.efteruddannelsestypenr,
        roundLessons(
          (byType.get(linje.efteruddannelsestypenr) ?? 0) +
            linje.indberettedeLektioner,
        ),
      );
    });
  });

  return byType;
};
